import { Result, err, ok } from '@hereisyourgitanton/core';
import { Observable, map, forkJoin } from 'rxjs';
import { TradingQueryPort } from '@tradgine/application-english-auction-trade';
import { inject } from '@angular/core';
import { AuctionApi } from '@tradgine/data-access/auction-flow';
import {
    TradingProcedureApi,
    LotApi,
    TradingProcedureDocumentApi,
    OrganizationApi,
    OfferSellerApi,
} from '@tradgine/data-access/tradgine-data';
import { BidHistoryApi } from '@tradgine/data-access/history';
import { toTradingProcedure, toAuction, toAutoBid, toCurrentOrganizationOffer, toTradingProcedureRegistry, toOfferResult } from '../mappers/mappers';
import { AutoBidInstructionsApi } from '@tradgine/data-access/auto-bid';
import {
    TradingProcedureSnapshot,
    TradingProcedure,
    Lot,
    Auction, AutoBid, TradingProcedureDocument, TradingProcedureRegistry, CurrentOrganizationOffer,
    BidHistory, OrganizationInfo, OfferResult,
} from '@tradgine/application-english-auction-trade';
import {
    OfferApi,
} from '@tradgine/data-access/tradgine-data';

export class TradingQueryAdapter implements TradingQueryPort {
    private readonly organizationApi: OrganizationApi = inject(OrganizationApi);
    private readonly tradingProcedureApiService = inject(TradingProcedureApi);
    private readonly auctionsApiService = inject(AuctionApi);
    private readonly lotsApiService = inject(LotApi);
    private readonly autoBidApiService = inject(AutoBidInstructionsApi);
    private readonly tradingProcedureDocumentApiService = inject(TradingProcedureDocumentApi);
    private readonly offerApiService = inject(OfferApi);
    private readonly bidHistoryApi = inject(BidHistoryApi);
    private readonly offerSellerApi = inject(OfferSellerApi);

    getTradingProcedureSnapshot(tpId: number): Observable<Result<TradingProcedureSnapshot, Error>> {
        return forkJoin({
            tp: this.tradingProcedureApiService.getById(tpId).pipe(map((result) => result.map(toTradingProcedure))),
            organization: this.organizationApi.getCurrentOrganization(),
            lots: this.lotsApiService.getAll(tpId),
            auctions: this.auctionsApiService
                .getAll(tpId)
                .pipe(map((result) => result.map((auctions) => auctions.map(toAuction)))),
            autoBids: this.autoBidApiService
                .getAll(tpId)
                .pipe(map((result) => result.map((autoBids) => autoBids.map(toAutoBid)))),
            offers: this.offerApiService.getAll(tpId)
        }).pipe(
            map((forkJoin) => {
                if (Object.values(forkJoin).some((result) => !result.isOk())) {
                    return err<TradingProcedureSnapshot, Error>(new Error('Failed to load trading procedure data'));
                } else {
                    const tradingProcedure = forkJoin.tp.unwrap();
                    const lots = forkJoin.lots.unwrap();
                    const offersDto = forkJoin.offers.unwrap();

                    // Маппим offers из DTO в domain модель
                    const lotsById = new Map<number, Lot>(lots.map(lot => [lot.id, lot]));
                    const offers: CurrentOrganizationOffer[] = offersDto
                        .map(offerDto => {
                            const lot = lotsById.get(offerDto.lotId);
                            if (!lot) {
                                // Пропускаем offer, если лот не найден
                                return null;
                            }
                            return toCurrentOrganizationOffer(offerDto, lot, tradingProcedure);
                        })
                        .filter((offer): offer is CurrentOrganizationOffer => offer !== null);

                    const org = forkJoin.organization.unwrap();
                    const organizationId = org.id;
                    const role = organizationId === tradingProcedure.sellerOrganizationId ? 'seller' : 'buyer';

                    return ok<TradingProcedureSnapshot, Error>({
                        tradingProcedure: tradingProcedure,
                        organizationId,
                        organizationName: org.name,
                        role,
                        lots: lots,
                        auctions: forkJoin.auctions.unwrap(),
                        autoBids: forkJoin.autoBids.unwrap(),
                        offers: offers,
                    });
                }
            })
        );
    }

    getTradingProcedure(tpId: number): Observable<Result<TradingProcedure, Error>> {
        return this.tradingProcedureApiService.getById(tpId).pipe(map((result) => result.map(toTradingProcedure)));
    }

    getLots(tpId: number): Observable<Result<readonly Lot[], Error>> {
        return this.lotsApiService.getAll(tpId);
    }

    getAuctions(tpId: number): Observable<Result<readonly Auction[], Error>> {
        return this.auctionsApiService
            .getAll(tpId)
            .pipe(map((result) => result.map((auctions) => auctions.map(toAuction))));
    }

    getAuctionById(auctionId: number): Observable<Result<Auction, Error>> {
        return this.auctionsApiService.getById(auctionId).pipe(map((result) => result.map(toAuction)));
    }

    getAutoBids(tpId: number): Observable<Result<readonly AutoBid[], Error>> {
        return this.autoBidApiService
            .getAll(tpId)
            .pipe(map((result) => result.map((autoBids) => autoBids.map(toAutoBid))));
    }

    getTradingProcedureDocuments(tpId: number): Observable<Result<TradingProcedureDocument[], Error>> {
        return this.tradingProcedureDocumentApiService.getAll(tpId);
    }
    getTradingProcedureRegistry(tpId: number): Observable<Result<TradingProcedureRegistry, Error>> {
        return this.tradingProcedureDocumentApiService.getRegistryInfo(tpId).pipe(
            map((result) => result.map(toTradingProcedureRegistry))
        );
    }

    getRegistryFile(tpId: number): Observable<Result<Blob, Error>> {
        return this.tradingProcedureDocumentApiService.getRegistry(tpId).pipe(
            map((result) => result.mapErr((err) => new Error(err.message)))
        );
    }

    getCurrentOrganizationOffers(tpId: number): Observable<Result<CurrentOrganizationOffer[], Error>> {
        return forkJoin({
            tp: this.tradingProcedureApiService.getById(tpId).pipe(map((result) => result.map(toTradingProcedure))),
            lots: this.lotsApiService.getAll(tpId),
            offers: this.offerApiService.getAll(tpId)
        }).pipe(
            map((forkJoin) => {
                if (Object.values(forkJoin).some((result) => !result.isOk())) {
                    return err<CurrentOrganizationOffer[], Error>(new Error('Failed to load offers'));
                } else {
                    const tradingProcedure = forkJoin.tp.unwrap();
                    const lots = forkJoin.lots.unwrap();
                    const offersDto = forkJoin.offers.unwrap();

                    // Маппим offers из DTO в domain модель
                    const lotsById = new Map<number, Lot>(lots.map(lot => [lot.id, lot]));
                    const offers: CurrentOrganizationOffer[] = offersDto
                        .map(offerDto => {
                            const lot = lotsById.get(offerDto.lotId);
                            if (!lot) {
                                // Пропускаем offer, если лот не найден
                                return null;
                            }
                            return toCurrentOrganizationOffer(offerDto, lot, tradingProcedure);
                        })
                        .filter((offer): offer is CurrentOrganizationOffer => offer !== null);

                    return ok<CurrentOrganizationOffer[], Error>(offers);
                }
            })
        );
    }

    getOrganizationId(): Observable<Result<number, Error>> {
        return this.organizationApi.getCurrentOrganization().pipe(
            map(result => result
                .map(org => org.id)
                .mapErr(() => new Error('Failed to fetch organization'))),
        )
    }

    getBidHistories(auctionId: number): Observable<Result<BidHistory[], Error>> {
        return this.bidHistoryApi.getAll(auctionId).pipe(
            map(result => result.map(dtos => dtos.map(dto => ({
                id: dto.id,
                auctionId: dto.auctionId,
                organizationId: dto.organizationId,
                postedDateTime: dto.postedDateTime,
                iteration: dto.iteration,
            }))))
        );
    }

    getSellerOrganizationInfos(tpId: number): Observable<Result<OrganizationInfo[], Error>> {
        return this.offerSellerApi.getAll(tpId).pipe(
            map(result => result.map(dtos => dtos.map(dto => ({
                organizationId: dto.organizationId,
                organizationName: dto.organizationName,
            }))))
        );
    }

    getOfferResults(tpId: number): Observable<Result<OfferResult[], Error>> {
        return this.offerApiService.getResults(tpId).pipe(
            map(result => result.map(dtos => dtos.map(toOfferResult)))
        );
    }
}
